// DF-0419 — Turnstile talks to challenges.cloudflare.com, never to us: a
// customer stuck on the anti-robot check left no trace anywhere on the
// platform, so « le bouton ne réagit pas » could not be counted. Each state of
// the checkout widget is posted to the hub's front-end diagnostic log
// (`core/frontendDiagnostic/`, AllowAny, 30 events/IP/min). Fire-and-forget:
// a failed report must never touch the order.

export type CaptchaDiagnosticState =
  | 'mounted'
  | 'token'
  | 'error'
  | 'timeout'
  | 'unsupported'
  | 'stalled'
  | 'interactive'
  | 'blocked_click'
  | 'retry';

export interface CaptchaDiagnostic {
  state: CaptchaDiagnosticState;
  restaurantUuid: string;
  /** Random id of this checkout visit, to stitch its events together. */
  session: string;
  /** ms since the widget was mounted on this visit. */
  elapsedMs: number;
  /** The help shown under the widget at that moment, `none` when nothing. */
  hint: string;
}

export function newCaptchaSession(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function reportCaptchaDiagnostic(d: CaptchaDiagnostic): void {
  const hub = import.meta.env.VITE_HUB_URL;
  if (!hub || typeof fetch !== 'function') return;
  try {
    void fetch(`${hub}/core/frontendDiagnostic/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        event: 'checkout_captcha',
        path: window.location.pathname,
        details: {
          message: d.state,
          state: d.state,
          restaurant_uuid: d.restaurantUuid,
          session: d.session,
          elapsed_ms: Math.round(d.elapsedMs),
          hint: d.hint,
        },
      }),
    }).catch(() => {});
  } catch {
    /* never let a diagnostic break the checkout */
  }
}

// DF-0573 — a refused order left nothing but `Bad Request` in sale_api's log,
// so which field a customer was stuck on could not be known. Same transport,
// same session id as the captcha events (one visit reads as one story). Only
// the status, the refused field paths and the server's own `code`/`detail`
// enums are sent — never anything the customer typed.
export interface SubmitErrorDiagnostic {
  restaurantUuid: string;
  session: string;
  status: number | string | undefined;
  fields: string[];
  code?: string;
  detail?: string;
}

export function reportSubmitErrorDiagnostic(d: SubmitErrorDiagnostic): void {
  const hub = import.meta.env.VITE_HUB_URL;
  if (!hub || typeof fetch !== 'function') return;
  try {
    void fetch(`${hub}/core/frontendDiagnostic/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        event: 'checkout_submit_error',
        path: window.location.pathname,
        details: {
          message: `${d.status ?? 'unknown'} ${d.fields.join(',') || d.code || d.detail || ''}`.trim(),
          status: d.status ?? null,
          fields: d.fields,
          code: d.code ?? null,
          detail: d.detail ?? null,
          restaurant_uuid: d.restaurantUuid,
          session: d.session,
        },
      }),
    }).catch(() => {});
  } catch {
    /* never let a diagnostic break the checkout */
  }
}
